import React, { MouseEvent, TouchEvent, useState } from 'react';
import styled, { css } from 'styled-components';
import { mediaQuery } from '@99designs/emu/css';
import { breakpoints } from '@99designs/emu/settings';

export interface CarouselItemsProps {
    items: React.ReactNode[];
    currentItemIndex: number;
    onSwipe?: (sitemIndex: number) => void;
    height?: string | Record<breakpoints.ScreenSize, string>;
}

const Container = styled.div<{ height?: string | Record<breakpoints.ScreenSize, string> }>`
    position: relative;
    width: 100%;
    margin: 0 auto;
    display: flex;
    align-items: center;

    ${({ height }) => {
        if (typeof height === 'string') {
            return css`
                overflow: hidden;
                height: ${height};
            `;
        }

        if (typeof height === 'object' && height !== null) {
            return css`
                overflow: hidden;

                ${mediaQuery.only('large')} {
                    height: ${height.large};
                }

                ${mediaQuery.only('normal')} {
                    height: ${height.normal};
                }

                ${mediaQuery.only('medium')} {
                    height: ${height.medium};
                }

                ${mediaQuery.only('small')} {
                    height: ${height.small};
                }
            `;
        }

        return null;
    }}
`;

const ItemWrapper = styled.div<{ visible: boolean; heightSet: boolean }>`
    position: ${({ visible }) => (visible ? 'relative' : 'absolute')};
    visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
    opacity: ${({ visible }) => (visible ? 1 : 0)};
    margin: ${({ visible }) => (visible ? '0 auto' : 'auto')};
    top: ${({ heightSet }) => (!heightSet ? '0' : 'inherit')};
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    transition-duration: 0.85s;
    transition-timing-function: ease-in-out;
    transition-property: opacity, visibility;
    will-change: opacity;

    img {
        user-select: none;
        user-drag: none;
    }
`;

const SWIPE_DISTANCE_PX = 100;

export function CarouselItems({ currentItemIndex, items, onSwipe, height }: CarouselItemsProps) {
    const [swipeStart, setSwipeStart] = useState<number | null>(null);

    const startMouseSwipe = (event: MouseEvent<HTMLDivElement>) => {
        setSwipeStart(event.clientX);
    };

    const endMouseSwipe = (event: MouseEvent<HTMLDivElement>) => {
        endSwipe(event.clientX);
    };

    const startTouchSwipe = (event: TouchEvent<HTMLDivElement>) => {
        setSwipeStart(event.touches[0].clientX);
    };

    const endTouchSwipe = (event: TouchEvent<HTMLDivElement>) => {
        endSwipe(event.changedTouches[0].clientX);
    };

    const endSwipe = (swipeEnd: number) => {
        if (!swipeStart || !(onSwipe instanceof Function)) {
            return;
        }

        const firstItemIndex = 0;
        const lastItemIndex = items.length - 1;
        const isFirstItem = currentItemIndex === firstItemIndex;
        const isLastItem = currentItemIndex === lastItemIndex;
        const swipeDistance = swipeStart - swipeEnd;

        if (swipeDistance > SWIPE_DISTANCE_PX) {
            // Swipe to the next item
            onSwipe(isLastItem ? firstItemIndex : currentItemIndex + 1);
        } else if (swipeDistance < SWIPE_DISTANCE_PX * -1) {
            // Swipe to the previous item
            onSwipe(isFirstItem ? lastItemIndex : currentItemIndex - 1);
        }

        setSwipeStart(null);
    };

    return (
        <Container
            height={height}
            onMouseDown={startMouseSwipe}
            onMouseUp={endMouseSwipe}
            onTouchStart={startTouchSwipe}
            onTouchEnd={endTouchSwipe}
        >
            {items.map((item, index) => (
                <ItemWrapper key={index} visible={currentItemIndex === index} heightSet={!!height}>
                    {item}
                </ItemWrapper>
            ))}
        </Container>
    );
}
